import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Logo from './Logo.jsx';
import FiveDotLoader from './partial/FiveDotLoader.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { closestOf, withoutProviderName } from '../_helpers/manualSongs.js';
import { capturePerformerEvent } from '../_helpers/performerEvents.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import '../styles/StreamerSongListImportPage.css';

export const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

/** The counts a completed job reports; the backend omits a count that is 0. */
const RESULT_COUNTS = ['imported', 'duplicates', 'unverified', 'failed'];

/**
 * Why a row was not imported, in the band's words (ENG-796). The row's own
 * `error` text is the server's and may name the song lookup's provider, so
 * it is never shown: the row's status and code pick a catalog message.
 */
const ROW_ERROR_KEYS = Object.freeze({
  'missing required field: title': 'importSonglist.results.reasonMissingTitle',
  'missing required field: artist': 'importSonglist.results.reasonMissingArtist',
});

const rowReason = (row, t) => {
  if (row.status === 'unverified') {
    const closest = closestOf(row);
    return closest
      ? t('importSonglist.results.reasonNotFoundDidYouMean', { title: closest.title, artist: closest.artist })
      : t('importSonglist.results.reasonNotFound');
  }
  if (row.errorCode === 'song_lookup_rejected') return t('importSonglist.results.reasonLookupRejected');
  if (row.verificationSkipped) return t('importSonglist.results.reasonLookupPaused');
  const key = ROW_ERROR_KEYS[String(row.error ?? '').trim().toLowerCase()];
  return t(key ?? 'importSonglist.results.reasonFailed');
};

/**
 * What a completed import did (ENG-512): the totals, and each row that was
 * not imported with the reason, so skipped rows are not missed on the way
 * back to the song list.
 */
const ImportResults = ({ result, unmatchedAction = null }) => {
  const { t, format } = useLocale();
  // Rows added as the band's own songs (ENG-795), counted in imported;
  // shown only when there were some, as the backend omits a 0.
  const counts = result.manual > 0 ? [...RESULT_COUNTS.slice(0, 1), 'manual', ...RESULT_COUNTS.slice(1)] : RESULT_COUNTS;
  // A row added afterwards with "Import them anyway" is listed as imported.
  const rows = (Array.isArray(result.problemRows) ? result.problemRows : []).filter((row) => row.status !== 'imported');

  return (
    <div className="ssl-import-results" data-testid="import-results">
      <dl className="ssl-import-results-totals">
        {counts.map((key) => (
          <div key={key} className="ssl-import-results-total" data-testid={`import-total-${key}`}>
            <dt>{t(`importSonglist.results.totals.${key}`)}</dt>
            <dd>{format.number(result[key] ?? 0)}</dd>
          </div>
        ))}
      </dl>
      {result.verificationDisabledReason && (
        <p className="ssl-import-results-note" role="note">
          {/* The server's reason names the provider; it is not shown. */}
          {t('importSonglist.results.verificationPaused', { count: result.verificationSkipped ?? 0 })}
        </p>
      )}
      {rows.length > 0 && (
        <table className="ssl-import-results-rows" aria-label={t('importSonglist.results.rowsLabel')}>
          <thead>
            <tr>
              <th scope="col">{t('importSonglist.results.columns.line')}</th>
              <th scope="col">{t('importSonglist.results.columns.song')}</th>
              <th scope="col">{t('importSonglist.results.columns.reason')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.row}>
                <td>{format.number(row.row)}</td>
                <td>{t('importSonglist.results.song', { title: row.title || '—', artist: row.artist || '—' })}</td>
                <td>{rowReason(row, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {result.problemRowsTruncated && (
        <p className="ssl-import-results-note">{t('importSonglist.results.truncated', { count: rows.length })}</p>
      )}
      {unmatchedAction}
    </div>
  );
};

/** Why "Import them anyway" failed (ENG-796), in the band's words. */
const addUnmatchedErrorKey = (error) => {
  switch (error?.code) {
    case 'import_job_expired':
      return 'importSonglist.results.addUnmatched.expired';
    case 'import_job_not_finished':
      return 'importSonglist.results.addUnmatched.notFinished';
    case 'import_job_not_found':
      return 'importSonglist.results.addUnmatched.notFound';
    default:
      break;
  }
  if (error?.status === 410) return 'importSonglist.results.addUnmatched.expired';
  return 'importSonglist.results.addUnmatched.failed';
};

/**
 * `songlist_song_added` for an import (ENG-888): one event per import, with
 * how many songs it added. A finished job is counted once, even when the page
 * is reloaded and reads it again.
 */
const IMPORT_TRACKED_PREFIX = 'songshake.importTracked.';
const trackImportedSongs = (bandid, fetchWrapper, jobId, count, step) => {
  if (!(count > 0)) return;
  const key = `${IMPORT_TRACKED_PREFIX}${jobId}.${step}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Without storage a reload may count it again; better than never.
  }
  void capturePerformerEvent('songlist_song_added', {
    bandId: bandid, fetchWrapper, source: 'csv_import', song_count: count,
  });
};

const countOf = (value) => (Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : 0);

function StreamerSongListImportPage() {
  const { bandid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fetchWrapper = useFetchWrapper();
  const { t } = useLocale();
  const { apiBaseUrl } = config;

  const jobId = searchParams.get('job_id');

  // Either { key } for a catalog message or { text } for the server's own error text.
  const [error, setErrorState] = useState(null);
  // The completed job (ENG-512): shown until the user goes back, rather
  // than navigating away the moment it finishes.
  const [result, setResult] = useState(null);
  // Keep the previous descriptor when the same error repeats so React bails out of the re-render,
  // exactly as it did when this state was a plain string.
  const setError = (next) =>
    setErrorState((prev) => (prev && prev.key === next.key && prev.text === next.text ? prev : next));
  const pageRef = useRef(null);
  // "Import them anyway" (ENG-796): the job's not-found rows added as the
  // band's own songs, from the stored job, so it works after a reload.
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    pageRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!jobId) {
      navigate(`/performers/${bandid}/song-list`, { replace: true });
      return;
    }

    // One flag per run of this effect (ENG-528). It was a ref shared by every
    // run, so the next run reset it to false and the previous run's in-flight
    // poll carried on: two polling loops (StrictMode mounts twice), and one
    // that outlived a change of job.
    let cancelled = false;
    let timeoutId = null;
    let pollCount = 0;
    setResult(null);
    setErrorState(null);
    setAdded(null);
    setAddError('');

    const poll = async () => {
      if (cancelled) return;
      pollCount += 1;
      if (pollCount > MAX_POLLS) {
        setError({ key: 'importSonglist.page.errors.tooLong' });
        return;
      }
      try {
        const result = await fetchWrapper.get(
          `${apiBaseUrl}/bands/${bandid}/import/songs/jobs/${jobId}`
        );
        if (cancelled) return;
        if (result.status === 'completed') {
          trackImportedSongs(bandid, fetchWrapper, jobId, countOf(result.imported) + countOf(result.manual), 'job');
          setResult(result);
        } else if (result.status === 'failed') {
          // The job's reason ("This file has N songs...") unless it names
          // the song lookup's provider.
          const text = withoutProviderName(result.error);
          setError(text ? { text } : { key: 'importSonglist.page.errors.failed' });
        } else {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        setError({ key: 'importSonglist.page.errors.statusCheckFailed' });
      }
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [jobId, bandid, fetchWrapper, apiBaseUrl, navigate]);

  const unmatchedCount = result?.unverified ?? 0;

  const importUnmatched = async () => {
    if (adding || !jobId) return;
    setAdding(true);
    setAddError('');
    try {
      const answer = await fetchWrapper.post(
        `${apiBaseUrl}/bands/${bandid}/import/songs/jobs/${jobId}/add-unmatched`
      );
      setAdded({ added: answer?.added ?? 0, failed: answer?.failed ?? 0 });
      trackImportedSongs(bandid, fetchWrapper, jobId, countOf(answer?.added), 'unmatched');
      // The stored job now counts those rows as imported.
      try {
        const refreshed = await fetchWrapper.get(`${apiBaseUrl}/bands/${bandid}/import/songs/jobs/${jobId}`);
        if (refreshed?.status === 'completed') setResult(refreshed);
      } catch {
        // The counts shown stay as they were; the rows were still added.
      }
    } catch (err) {
      setAddError(t(addUnmatchedErrorKey(err)));
    } finally {
      setAdding(false);
    }
  };

  const unmatchedAction = result && (unmatchedCount > 0 || added || addError) ? (
    <div className="ssl-import-results-unmatched" data-testid="import-unmatched">
      {unmatchedCount > 0 && (
        <>
          <p className="ssl-import-results-note">{t('importSonglist.results.addUnmatched.prompt', { count: unmatchedCount })}</p>
          <button
            type="button"
            className="ssl-import-page-btn-back"
            onClick={importUnmatched}
            disabled={adding}
            data-testid="import-unmatched-anyway"
          >
            {adding ? t('importSonglist.results.addUnmatched.adding') : t('importSonglist.results.addUnmatched.action')}
          </button>
        </>
      )}
      {added && (
        <p className="ssl-import-results-note" role="status" data-testid="import-unmatched-added">
          {t('importSonglist.results.addUnmatched.added', { count: added.added })}
          {added.failed > 0 && ` ${t('importSonglist.results.addUnmatched.someFailed', { count: added.failed })}`}
        </p>
      )}
      {addError && <p className="ssl-import-page-error" role="alert">{addError}</p>}
    </div>
  ) : null;

  const errorMessage = error ? (error.text ?? t(error.key)) : '';
  const done = Boolean(errorMessage || result);
  const title = errorMessage
    ? t('importSonglist.page.failed')
    : result
      ? t('importSonglist.results.title')
      : t('importSonglist.page.importing');

  return (
    <div className="ssl-import-page" data-theme="songshake" ref={pageRef} tabIndex={-1}>
      <div className="ssl-import-page-hero">
        <Logo />
        {!done && (
          <FiveDotLoader className="ssl-import-dot-loader" ariaLabel={t('importSonglist.page.loaderLabel')} />
        )}
        <h1 className="ssl-import-page-title">{title}</h1>
        {errorMessage ? (
          <p className="ssl-import-page-error" role="alert" data-testid="import-page-error">{errorMessage}</p>
        ) : result ? (
          <ImportResults result={result} unmatchedAction={unmatchedAction} />
        ) : (
          <p className="ssl-import-page-subtitle">
            {t('importSonglist.page.subtitle')}
          </p>
        )}
        {done && (
          <button
            type="button"
            className="ssl-import-page-btn-back"
            onClick={() => navigate(`/performers/${bandid}/song-list`)}
          >
            {t('importSonglist.page.backToSongList')}
          </button>
        )}
      </div>
      <footer className="ssl-import-page-footer">
        <div className="ssl-import-page-footer-left">
          <a href="/terms" className="ssl-import-page-footer-link">{t('footer.termsOfService')}</a>
          <a href="/privacy" className="ssl-import-page-footer-link">{t('footer.privacyPolicy')}</a>
        </div>
        <div className="ssl-import-page-footer-right">
          <a
            href="https://discord.gg/YpbSrpmMJc"
            className="ssl-import-page-footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.social.discord')}
          </a>
          <a
            href="https://twitter.com/songshake"
            className="ssl-import-page-footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.social.twitter')}
          </a>
        </div>
      </footer>
    </div>
  );
}

export default StreamerSongListImportPage;
